import React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useFetch from "../hooks/useFetch";
import { v4 as uuidv4 } from "uuid";
import SimpleBar from "simplebar-react";
import { stackItemsTranslate } from "../js/stack-list";
import AnimatedContent from "./animation/AnimatedContent";

export default function CasesSection() {
  const navigate = useNavigate();
  const { slug: urlSlug } = useParams();
  const didDeepLink = useRef(false);
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 1099px)").matches;

  // На планшете/мобиле (≤991) показываем только кейсы с CMS-флагом ShowOnMobile.
  // Скрытые НЕ рендерим (а не display:none) — иначе ступенчатые span'ы у видимых
  // карточек съезжают (nth-child считал бы скрытые), и раскладка ломалась.
  const [hideOnMobile, setHideOnMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width: 991px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991px)");
    const onChange = () => setHideOnMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);


  const MaybeAnimate = ({ enabled, children, ...props }) =>
    enabled ? <AnimatedContent {...props}>{children}</AnimatedContent> : children;

  useLayoutEffect(() => {
    stackItemsTranslate();

    const casesCards = document.querySelectorAll(".cases-card");
    const aside = document.querySelector(".aside");
    const body = document.querySelector("body");

    const openModal = (modal) => {
      modal.classList.add("open");
      body.classList.add("cases-modal-open");
      body.classList.add("no-scroll");
      if (!isMobile) {
        aside.classList.add("none");
      } else {
        document
          .getElementById("cases")
          .scrollIntoView({ behavior: "smooth" });
        document.querySelector(".header-mobile").classList.add("under");
      }
    };

    const closeModal = (modal) => {
      modal.classList.remove("open");
      body.classList.remove("cases-modal-open");
      body.classList.remove("no-scroll");
      if (!isMobile) {
        setTimeout(() => {
          aside.classList.remove("none");
        }, 200);
      } else {
        document.querySelector(".header-mobile").classList.remove("under");
      }
    };

    if (casesCards.length) {
      casesCards.forEach((card) => {
        const img = card.querySelector(".cases-card__img");
        const title = card.querySelector(".cases-card__title");
        const modal = card.querySelector(".cases-modal");
        const slug = card.dataset.slug;

        if (modal) {
          const closeBtn = modal.querySelector(".cases-modal__close");

          const open = () => {
            openModal(modal);
            if (slug) navigate("/case/" + slug);
          };

          img.addEventListener("click", open);
          title.addEventListener("click", open);

          closeBtn.addEventListener("click", () => {
            closeModal(modal);
            navigate("/");
          });
        }
      });

      // Deep-link: при заходе по /case/:slug открыть нужный кейс один раз
      if (urlSlug && !didDeepLink.current) {
        const target = [...casesCards].find(
          (card) => card.dataset.slug === urlSlug
        );
        const modal = target && target.querySelector(".cases-modal");

        if (modal) {
          didDeepLink.current = true;
          const cases = document.getElementById("cases");
          if (cases) cases.scrollIntoView();
          openModal(modal);
        }
      }
    }
  });

  const casesFetch = useFetch(
    `${window.$host}/api/case?populate[Cases][populate]=PreviewPicture&populate[Cases][populate]=Tags&populate[Cases][populate]=Stack.Icon`
  );

  if (casesFetch.loading) return;
  if (casesFetch.error) return;

  return (
    <section className="cases" id="cases">
      <div className="container">
        <div className="h-100 content-box">
          <div className="cases__box">
            <MaybeAnimate enabled={!isMobile} delay={0.10}>
              <h2 className="title-4 cases__title">
                {casesFetch.data.data.Title}{" "}
                <span className="text-blue">
                  {casesFetch.data.data.TitleBlue}
                </span>
              </h2>
            </MaybeAnimate>
            <ul className="cases__list">
              {casesFetch.data.data.Cases.slice(0, 6)
                .filter((item) => !hideOnMobile || item.ShowOnMobile === true)
                .map((item, index) => {
                const rowDelay = 0.30 + Math.floor(index / 3) * 0.20;

                return (
                  <li
                    key={item.id}
                    data-slug={item.Slug || item.id}
                    className={[
                      "cases-card",
                      item.IsModal === true ? "" : "cases-card-no-pointer",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <MaybeAnimate enabled={!isMobile} delay={rowDelay}>
                      <>
                        <div className="cases-card__img">
                          <img
                            src={window.$host + item.PreviewPicture.url}
                            alt={item.PreviewPicture.alternativeText}
                          />
                        </div>
                        <h3 className="text-16 cases-card__title">
                          {item.PreviewTitle}
                        </h3>
                      </>
                    </MaybeAnimate>

                    {item.IsModal ? (
                      <SimpleBar className="cases-modal">
                        <div className="cases-modal-close-box">
                          <button className="cases-modal__close">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <circle
                                cx="4"
                                cy="4"
                                r="1"
                                transform="rotate(-90 4 4)"
                                fill="white"
                              />
                              <circle
                                cx="12"
                                cy="4"
                                r="1"
                                transform="rotate(-90 12 4)"
                                fill="white"
                              />
                              <circle
                                cx="6"
                                cy="6"
                                r="1"
                                transform="rotate(-90 6 6)"
                                fill="white"
                              />
                              <circle
                                cx="8"
                                cy="8"
                                r="1"
                                transform="rotate(-90 8 8)"
                                fill="white"
                              />
                              <circle
                                cx="10"
                                cy="6"
                                r="1"
                                transform="rotate(-90 10 6)"
                                fill="white"
                              />
                              <circle
                                cx="6"
                                cy="10"
                                r="1"
                                transform="rotate(-90 6 10)"
                                fill="white"
                              />
                              <circle
                                cx="10"
                                cy="10"
                                r="1"
                                transform="rotate(-90 10 10)"
                                fill="white"
                              />
                              <circle
                                cx="4"
                                cy="12"
                                r="1"
                                transform="rotate(-90 4 12)"
                                fill="white"
                              />
                              <circle
                                cx="12"
                                cy="12"
                                r="1"
                                transform="rotate(-90 12 12)"
                                fill="white"
                              />
                              <circle
                                cx="2.5"
                                cy="2.5"
                                r="0.5"
                                transform="rotate(-90 2.5 2.5)"
                                fill="white"
                              />
                              <circle
                                cx="13.5"
                                cy="2.5"
                                r="0.5"
                                transform="rotate(-90 13.5 2.5)"
                                fill="white"
                              />
                              <circle
                                cx="13.5"
                                cy="13.5"
                                r="0.5"
                                transform="rotate(-90 13.5 13.5)"
                                fill="white"
                              />
                              <circle
                                cx="2.5"
                                cy="13.5"
                                r="0.5"
                                transform="rotate(-90 2.5 13.5)"
                                fill="white"
                              />
                            </svg>
                          </button>
                        </div>

                        <div className="container">
                          <div className="cases-modal-wrapper">
                            <aside className="cases-modal-aside">
                              <div className="cases-modal-aside__box">
                                <div className="cases-modal-aside__content">
                                  {item.Client ? (
                                    <div className="cases-modal-aside__line">
                                      <h3 className="text-12 text-gray">
                                        заказчик
                                      </h3>
                                      <p className="text-16">{item.Client}</p>
                                    </div>
                                  ) : (
                                    ""
                                  )}

                                  {item.Time ? (
                                    <div className="cases-modal-aside__line">
                                      <h3 className="text-12 text-gray">
                                        срок реализации
                                      </h3>
                                      <p className="text-16">{item.Time}</p>
                                    </div>
                                  ) : (
                                    ""
                                  )}

                                  {item.Stack.length ? (
                                    <div className="cases-modal-aside__line">
                                      <h3 className="text-12 text-gray">Стек</h3>
                                      <ul className="stack-icons-list">
                                        {item.Stack.map((stack) => (
                                          <li key={stack.id}>
                                            <img
                                              src={window.$host + stack.Icon.url}
                                              alt={stack.Name}
                                            />
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : (
                                    ""
                                  )}

                                  {item.Problem ? (
                                    <div className="cases-modal-aside__line">
                                      <h3 className="text-12 text-gray">
                                        проблема
                                      </h3>
                                      <p className="text-16">{item.Problem}</p>
                                    </div>
                                  ) : (
                                    ""
                                  )}

                                  {item.Link ? (
                                    <a
                                      className="dark-arrow"
                                      href={item.Link}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <span className="dark-arrow__text">
                                        Открыть сайт
                                      </span>
                                      <span className="dark-arrow__arrow">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="17"
                                          height="12"
                                          viewBox="0 0 17 12"
                                          fill="none"
                                        >
                                          <circle
                                            cx="4"
                                            cy="6"
                                            r="1"
                                            transform="rotate(90 4 6)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="1"
                                            cy="6"
                                            r="1"
                                            transform="rotate(90 1 6)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="7"
                                            cy="6"
                                            r="1"
                                            transform="rotate(90 7 6)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="10"
                                            cy="6"
                                            r="1"
                                            transform="rotate(90 10 6)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="13"
                                            cy="6"
                                            r="1"
                                            transform="rotate(90 13 6)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="16"
                                            cy="6"
                                            r="1"
                                            transform="rotate(90 16 6)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="13"
                                            cy="9"
                                            r="1"
                                            transform="rotate(90 13 9)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="13"
                                            cy="3"
                                            r="1"
                                            transform="rotate(90 13 3)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="10"
                                            cy="0.500001"
                                            r="0.5"
                                            transform="rotate(90 10 0.500001)"
                                            fill="white"
                                          />
                                          <circle
                                            cx="10"
                                            cy="11.5"
                                            r="0.5"
                                            transform="rotate(90 10 11.5)"
                                            fill="white"
                                          />
                                        </svg>
                                      </span>
                                    </a>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              </div>
                            </aside>
                            <div className="cases-modal-content-wrapper">
                              <div className="cases-modal-content">
                                <div className="cases-modal-content__box">
                                  <img
                                    src={window.$host + item.PreviewPicture.url}
                                    alt={item.PreviewPicture.alternativeText}
                                  />
                                  <h3>{item.PreviewTitle}</h3>
                                  {item.Tags.length ? (
                                    <ul className="cases-modal-content__tags-list">
                                      {item.Tags.map((li) => (
                                        <li key={li.id}>{li.Name}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    ""
                                  )}

                                  {item.Description.map((part) => (
                                    <div key={uuidv4()}>
                                      {part.type === "paragraph" && (
                                        <p>{part.children[0].text}</p>
                                      )}
                                      {part.type === "heading" && (
                                        <h3>{part.children[0].text}</h3>
                                      )}
                                      {part.type === "image" && (
                                        <img
                                          src={part.image.url}
                                          alt={part.image.alternativeText}
                                        />
                                      )}
                                      {part.type === "list" && (
                                        <ul>
                                          {part.children.map((item) => (
                                            <li key={uuidv4()}>
                                              {item.children[0].text}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </SimpleBar>
                    ) : (
                      ""
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
